import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { useHistory } from 'react-router-dom';
import { client } from "../../hooks/Client";
import CompagnyMeta from '../../components/portfolio/CompagnyMeta'
import { useFirestoreNoCompagnyGeneralTwoOrderBy } from '../../firebase/useFirestore';
import EffectMeta from '../../components/effects/EffectMeta';
import { db } from '../../firebase/config';
import firebase from '../../firebase/compat';
import OutputMeta from '../../components/outputs/OutputMeta';
import FieldMeta from '../../components/Questionnaire/FieldMeta';
import TargetgroupMeta from '../../components/stakeholders/TargetgroupMeta';
import CustomEventFieldMeta from '../../components/events/CustomEventFieldMeta';
import updateRequestStatusLabel from '../../helpers/updateRequests/updateRequestStatus';
import { isPersonaAlertType, personaAlertSegments, isMomentAlertType, momentAlertSegments } from '../../helpers/personas/personaAlertText';

const Notifications = () => {
  // Hooks
  const history = useHistory()

  // Firestore
  const notifications = useFirestoreNoCompagnyGeneralTwoOrderBy(
    'Notifications',
    'Reciever',
    client,
    'Read',
    false,
    'Timestamp',
    'desc'
  );

  // Show sync request
  const showSyncRequest = async (e) => {
    const docid = e.currentTarget.dataset.docid
    const id = e.currentTarget.dataset.id

    await db.collection('Notifications').doc(docid).update({
      Read: true
    })

    history.push(`/${client}/synchronisationbuilder/${id}`)
  }

  // Show sync copy (bulk)
  const showSyncCopy = async (e) => {
    const docid = e.currentTarget.dataset.docid

    await db.collection('Notifications').doc(docid).update({
      Read: true
    })

    history.push(`/${client}/synchronisations`)
  }

  // Show update request status change
  // AI-CONTEXT: See docs/features/update-aanvragen.md
  const showUpdateRequest = async (e) => {
    const docid = e.currentTarget.dataset.docid

    await db.collection('Notifications').doc(docid).update({
      Read: true
    })

    // Naar het tabblad zelf, niet naar het verzoek: de Updates-pagina leest het
    // geopende paneel uit de URL, dus een losse link werkt.
    history.push(`/${client}/updates?tab=mijn-updates`)
  }

  // Naar de deelnemer achter een melding over een verlopend identiteitsbewijs
  // of een verjaardag. De personabuilder-pagina is dezelfde als vanuit de lijst.
  const showPersona = async (e) => {
    const docid = e.currentTarget.dataset.docid
    const personaId = e.currentTarget.dataset.personaid

    await db.collection('Notifications').doc(docid).update({
      Read: true
    })

    history.push(`/${client}/personabuilder/${personaId}`)
  }

  // Meetmoment: meteen naar het tabblad Onderzoeken van de deelnemer, want
  // daar staat wat er openstaat en de deel-link om de meting uit te zetten.
  const showPersonaResearch = async (e) => {
    const docid = e.currentTarget.dataset.docid
    const personaId = e.currentTarget.dataset.personaid

    await db.collection('Notifications').doc(docid).update({
      Read: true
    })

    history.push(`/${client}/personabuilder/${personaId}?tab=research`)
  }

  // Show a newly submitted update request (Deccos only)
  // AI-CONTEXT: See docs/features/update-aanvragen.md
  const showNewUpdateRequest = async (e) => {
    const docid = e.currentTarget.dataset.docid

    await db.collection('Notifications').doc(docid).update({
      Read: true
    })

    // Naar het dashboard: daar staat de kaart 'Updateverzoeken', waar de status
    // ook gezet wordt. Er is geen losse pagina per verzoek om heen te linken.
    history.push(`/${client}/dashboard`)
  }

  // Accept parent request
  const acceptParentRequest = async (e) => {
    const docid = e.currentTarget.dataset.docid
    const parent = e.currentTarget.dataset.parent
    
    const compagnySnapshot = await db.collection('CompagnyMeta')
      .where('CompagnyID', '==', client)
      .get()

    compagnySnapshot.docs.forEach(doc => {
      db.collection('CompagnyMeta').doc(doc.id).update({
        Parent: firebase.firestore.FieldValue.arrayUnion(parent)
      })
    })

    await db.collection('Notifications').doc(docid).update({
      Read: true
    })
  }

  // Sync type label
  const syncTypes = (type) => {
    switch (type) {
      case 'Effect':
        return 'Effect';
      case 'MSIEffect':
        return 'Meetstandaard effect';
      case 'Output':
        return 'Output';
      case 'Question':
        return 'Vraag';
      case 'Targetgroup':
        return 'Doelgroep';
      case 'CustomEvent':
        return 'Custom veld';
      case 'Activity':
        return 'Activiteit';
      default:
        return 'Onbekend';
    }
  }

  // Notification type component
  /**
   * Eén melding, getekend naar zijn Type. Alle velden komen gedenormaliseerd van
   * het Notifications-document, zodat de lijst geen lookup per regel doet.
   *
   * @param {object} props
   * @param {string} props.type - `Notifications.Type`; bepaalt welke tak rendert
   * @param {string} props.parent - `Sender`: de organisatie die de melding stuurde
   * @param {string} [props.id] - `SyncId` bij synchronisatiemeldingen
   * @param {string} [props.effectId] - Gekoppeld effect (sync)
   * @param {string} [props.outputId] - Gekoppelde output (sync)
   * @param {string} [props.questionId] - Gekoppelde vraag (sync)
   * @param {string} [props.targetgroupId] - Gekoppelde doelgroep (sync)
   * @param {string} [props.customEventId] - Gekoppelde activiteit (sync)
   * @param {string} [props.syncType] - Soort synchronisatie
   * @param {object} [props.counts] - Aantallen per soort bij een sync-kopie
   * @param {string} props.docid - Firestore-docid, om de melding op gelezen te zetten
   * @param {string} [props.requestTitle] - Titel van het updateverzoek
   * @param {string} [props.requestStatus] - Status van het updateverzoek
   * @param {string} [props.compagnyName] - Naam van de indienende organisatie (updateverzoek)
   * @param {string} [props.personaId] - De deelnemer bij een deelnemersmelding
   * @param {string} [props.personaName] - Naam van die deelnemer
   * @param {string} [props.alertDate] - Verloop- of geboortedatum, `YYYY-MM-DD`
   * @param {number} [props.daysLeft] - Dagen tot het identiteitsbewijs verloopt
   * @param {number} [props.age] - Leeftijd die de deelnemer wordt
   * @param {string} [props.momentTitle] - Titel van het meetmoment (meetmomentmelding)
   * @param {string} [props.researchTitle] - Titel van het onderzoek (meetmomentmelding)
   * @param {number|null} [props.daysUntilDue] - Aftelling tot het meetmoment; negatief = te laat
   */
  const NotificationType = ({
    type,
    parent,
    id,
    effectId,
    outputId,
    questionId,
    targetgroupId,
    customEventId,
    syncType,
    counts,
    docid,
    requestTitle,
    requestStatus,
    compagnyName,
    personaId,
    personaName,
    alertDate,
    daysLeft,
    age,
    momentTitle,
    researchTitle,
    daysUntilDue
  }) => {

    if (type === "parent-request") {
      return (
        <div className='notification-item'>
          <h2>Portfolio aanvraag</h2>
          <p>
            <CompagnyMeta id={parent} /> wil <CompagnyMeta id={client} /> toevoegen aan zijn porfolio
          </p>
          <button data-docid={docid} data-parent={parent} onClick={acceptParentRequest}>Accepteren</button>
          <button>Weigeren</button>
        </div>
      );
    } else if (type === "delete-project") {
      return (
        <div className='notification-item'>
          <h2>Portfolio verwijder verzoek</h2>
          <p>
            <CompagnyMeta id={parent} /> wil <CompagnyMeta id={client} /> verwijderen uit zijn portfolio
          </p>
          <button>Verzoek behandelen</button>
        </div>
      )
    } else if (type === "sync-request") {
      return (
        <div className='notification-item'>
          <h2>Synchronisatie verzoek</h2>
          <p>
            <CompagnyMeta id={parent} /> wil een synchronisatie maken voor zijn {syncTypes(syncType)}:{' '}
            {syncType === 'Effect' ? (
              <b><EffectMeta item={effectId} /></b>
            ) : syncType === 'Output' ? (
              <b><OutputMeta id={outputId} /></b>
            ) : syncType === 'Question' ? (
              <b><FieldMeta item={questionId} /></b>
            ) : syncType === 'MSIEffect' ? (
              <b><EffectMeta item={effectId} /></b>
            ) : syncType === 'Targetgroup' ? (
              <b><TargetgroupMeta targetgroupID={targetgroupId} /></b>
            ) : syncType === 'CustomEvent' ? (           
              <CustomEventFieldMeta fieldId={customEventId} />
            ) : null}
          </p>
          <button data-id={id} data-docid={docid} onClick={showSyncRequest}>
            Verzoek behandelen
          </button>
        </div>
      )
    } else if (type === "sync-request-batch") {
      const countEntries = Object.entries(counts || {}).filter(([, n]) => n > 0);
      return (
        <div className='notification-item'>
          <h2>Synchronisatie verzoek</h2>
          <p>
            <CompagnyMeta id={parent} /> wil de volgende items synchroniseren met jouw project:
          </p>
          {countEntries.length > 0 && (
            <ul>
              {countEntries.map(([countType, n]) => (
                <li key={countType}>{n} × {syncTypes(countType)}</li>
              ))}
            </ul>
          )}
        </div>
      )
    } else if (type === "sync-subscribe") {
      const countEntries = Object.entries(counts || {}).filter(([, n]) => n > 0);
      return (
        <div className='notification-item'>
          <h2>Project heeft items overgenomen</h2>
          <p>
            <CompagnyMeta id={parent} /> heeft items uit jouw portfolio overgenomen:
          </p>
          {countEntries.length > 0 && (
            <ul>
              {countEntries.map(([countType, n]) => (
                <li key={countType}>{n} × {syncTypes(countType)}</li>
              ))}
            </ul>
          )}
        </div>
      )
    } else if (type === "sync-copy") {
      return (
        <div className='notification-item'>
          <h2>Nieuwe synchronisatie verzoeken</h2>
          <p>
            <CompagnyMeta id={parent} /> heeft nieuwe synchronisatie verzoeken gestuurd.
          </p>
          <button data-docid={docid} onClick={showSyncCopy}>
            Bekijken
          </button>
        </div>
      )
    } else if (type === "update-request-status") {
      return (
        <div className='notification-item'>
          <h2>Je updateverzoek is bijgewerkt</h2>
          <p>
            {/* De titel staat gedenormaliseerd op de melding zelf, zodat de
                lijst geen lookup per regel hoeft te doen. Ontbreekt hij, dan
                moet de zin nog steeds lopen. */}
            {requestTitle ? <b>{requestTitle}</b> : 'Een van je aanvragen'} staat nu op{' '}
            <b>{updateRequestStatusLabel(requestStatus)}</b>.
          </p>
          <button data-docid={docid} onClick={showUpdateRequest}>
            Bekijken
          </button>
        </div>
      )
    } else if (type === "update-request-new") {
      return (
        <div className='notification-item'>
          <h2>Nieuw updateverzoek</h2>
          <p>
            {/* De naam staat gedenormaliseerd op de melding, zodat de lijst geen
                lookup per regel doet. Een oudere melding heeft hem niet; dan
                alsnog opzoeken in plaats van een gat laten. */}
            {compagnyName ? <b>{compagnyName}</b> : <CompagnyMeta id={parent} />} heeft een
            updateverzoek ingediend{requestTitle ? <>: <b>{requestTitle}</b></> : null}.
          </p>
          <button data-docid={docid} onClick={showNewUpdateRequest}>
            Bekijken
          </button>
        </div>
      )
    } else if (isPersonaAlertType(type)) {
      // Verlopend identiteitsbewijs of verjaardag van een deelnemer; de zin komt
      // uit dezelfde helper als de mail, met de naam los zodat hij vet kan.
      const segments = personaAlertSegments({ Type: type, PersonaName: personaName, Date: alertDate, DaysLeft: daysLeft, Age: age })
      return (
        <div className='notification-item'>
          <h2>{segments.heading}</h2>
          <p>
            {segments.before}<b>{segments.name}</b>{segments.after}
          </p>
          <button data-docid={docid} data-personaid={personaId} onClick={showPersona}>
            Bekijken
          </button>
        </div>
      )
    } else if (isMomentAlertType(type)) {
      // Meetmoment dat bijna zover of te laat is; dezelfde zin als in de mail.
      // 'Bekijken' opent het tabblad Onderzoeken van de deelnemer — daar staan
      // de chips per meetmoment en de deel-link.
      const segments = momentAlertSegments({ Type: type, PersonaName: personaName, MomentTitle: momentTitle, ResearchTitle: researchTitle, DaysUntilDue: daysUntilDue })
      return (
        <div className='notification-item'>
          <h2>{segments.heading}</h2>
          <p>
            {segments.before}<b>{segments.name}</b>{segments.after}
          </p>
          <button data-docid={docid} data-personaid={personaId} onClick={showPersonaResearch}>
            Bekijken
          </button>
        </div>
      )
    } else {
      return <div />
    }
  };

  return (
    <div className="main">
      <div className="main-container">
        <div className="page-header">
          <NotificationsNoneOutlinedIcon />
          <h1>Notificaties</h1>
        </div>
        {/* De lijst toont alleen ongelezen meldingen (de query filtert op
            Read == false) en 'Bekijken' zet een melding op gelezen. Zonder
            deze zin leek een melding stil te verdwijnen — testronde
            2026-10-01, B2. */}
        <p className='notifications-intro'>
          Hier staan alleen je ongelezen meldingen. Klik je op 'Bekijken', dan is de melding afgehandeld en verdwijnt hij uit deze lijst.
        </p>
        <div id='notifications-container'>
          {/* Sleutel op het docid, niet op het ID-veld: een oudere melding
              heeft dat veld niet en gaf een key-warning (testronde 2026-10-01, B6). */}
          {notifications && notifications.map((notification) => (
            <div key={notification.docid} className='notification-item-container'>
              <NotificationType
                type={notification.Type}
                parent={notification.Sender}
                id={notification.SyncId}
                effectId={notification.Effect}
                outputId={notification.Output}
                questionId={notification.Question}
                targetgroupId={notification.Targetgroup}
                customEventId={notification.CustomEvent}
                syncType={notification.SyncType}
                counts={notification.Counts}
                docid={notification.docid}
                requestTitle={notification.RequestTitle}
                requestStatus={notification.Status}
                compagnyName={notification.CompagnyName}
                personaId={notification.PersonaID}
                personaName={notification.PersonaName}
                alertDate={notification.Date}
                daysLeft={notification.DaysLeft}
                age={notification.Age}
                momentTitle={notification.MomentTitle}
                researchTitle={notification.ResearchTitle}
                daysUntilDue={notification.DaysUntilDue}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Notifications
